import type {AiChatStateContextType} from '@AiChat/providers/ai-chat-provider.types';
import {InspirationLayoutVariant, InspirationWidthVariant} from '@AiChat/components/ai-chat-window-inspirations';
import {ButtonType} from '@Components/button';

export const AI_CHAT_SEASON_SUGGESTIONS_PER_SET = 3;
export const AI_CHAT_SEASON_MOBILE_INSPIRATION_COUNT = 3;

export enum AiChatSeason {
  HALLOWEEN = 'halloween',
  CHRISTMAS = 'christmas',
}

export enum AiChatHalloweenAsset {
  SPIDER_WEB = 'ai-chat-halloween-spider-web.webp',
  SPIDER = 'ai-chat-halloween-spider.webp',
  PUMPKIN_LANTERN = 'ai-chat-halloween-pumpkin-lantern.webp',
  PUMPKIN_ANGRY = 'ai-chat-halloween-pumpkin-angry.webp',
  PUMPKIN_HAPPY = 'ai-chat-halloween-pumpkin-happy.webp',
}

enum AiChatHalloweenCard {
  BAKERY_MENU = 'ai-chat-halloween-card-bakery-menu.webp',
  PARTY_POSTER = 'ai-chat-halloween-card-party-poster.webp',
  PROMO_IDEAS = 'ai-chat-halloween-card-promo-ideas.webp',
  CAPTION = 'ai-chat-halloween-card-caption.webp',
}

export enum AiChatChristmasAsset {
  SNOWMAN = 'ai-chat-christmas-snowman.webp',
  SNOWMAN_ARM = 'ai-chat-christmas-snowman-arm.webp',
  TREE = 'ai-chat-christmas-tree.webp',
  GIFTS = 'ai-chat-christmas-gifts.webp',
  SNOW_BANK = 'ai-chat-christmas-snow-bank.webp',
  SNOW_FLOOR = 'ai-chat-christmas-snow-floor.webp',
}

enum AiChatChristmasCard {
  BAKERY_MENU = 'ai-chat-christmas-card-bakery-menu.webp',
  PARTY_POSTER = 'ai-chat-christmas-card-party-poster.webp',
  PROMO_IDEAS = 'ai-chat-christmas-card-promo-ideas-padded.webp',
  CAPTION = 'ai-chat-christmas-card-caption-padded.webp',
}

export type AiChatEmptyHomeState = Pick<AiChatStateContextType, 'currentChatId' | 'isPromptBoxOnly' | 'isQuickTipsView' | 'isAssetsView' | 'isMemoryView'>;

export type AiChatSeasonPageState = AiChatEmptyHomeState & Pick<AiChatStateContextType, 'previewSeason'>;

export interface AiChatSeasonDates {
  startMonthIndex: number;
  startDay: number;
  endMonthIndex: number;
  endDay: number;
}

export interface AiChatSeasonSuggestion {
  emoji: string;
  titleKey: string;
  promptKey: string;
}

export interface AiChatSeasonInspiration {
  card: string;
  visualLayoutVariant: InspirationLayoutVariant;
  widthVariant: InspirationWidthVariant;
  pillTextKey: string;
  pillIcon: string;
  promptKey: string;
}

export interface AiChatSeasonConfig {
  dates: AiChatSeasonDates;
  suggestions: AiChatSeasonSuggestion[];
  inspirations: AiChatSeasonInspiration[];
  loginButtonType: ButtonType;
}

const AI_CHAT_HALLOWEEN_SUGGESTIONS: AiChatSeasonSuggestion[] = [
  {emoji: '🎃', titleKey: 'pmwjs_ai_chat_halloween_suggestion_costume_party_poster', promptKey: 'pmwjs_ai_chat_halloween_suggestion_costume_party_poster_prompt'},
  {emoji: '👻', titleKey: 'pmwjs_ai_chat_halloween_suggestion_cute_ghost_sticker', promptKey: 'pmwjs_ai_chat_halloween_suggestion_cute_ghost_sticker_prompt'},
  {emoji: '💡', titleKey: 'pmwjs_ai_chat_halloween_suggestion_promo_ideas', promptKey: 'pmwjs_ai_chat_halloween_suggestion_promo_ideas_prompt'},
  {emoji: '🦇', titleKey: 'pmwjs_ai_chat_halloween_suggestion_haunted_house_flyer', promptKey: 'pmwjs_ai_chat_halloween_suggestion_haunted_house_flyer_prompt'},
  {emoji: '🎃', titleKey: 'pmwjs_ai_chat_halloween_suggestion_pumpkin_logo', promptKey: 'pmwjs_ai_chat_halloween_suggestion_pumpkin_logo_prompt'},
  {emoji: '✍️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_instagram_caption', promptKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_instagram_caption_prompt'},
  {emoji: '🍬', titleKey: 'pmwjs_ai_chat_halloween_suggestion_trick_or_treat_poster', promptKey: 'pmwjs_ai_chat_halloween_suggestion_trick_or_treat_poster_prompt'},
  {emoji: '🕯️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_cafe_photo', promptKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_cafe_photo_prompt'},
  {emoji: '💡', titleKey: 'pmwjs_ai_chat_halloween_suggestion_sale_ideas', promptKey: 'pmwjs_ai_chat_halloween_suggestion_sale_ideas_prompt'},
  {emoji: '🛍️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_sale_post', promptKey: 'pmwjs_ai_chat_halloween_suggestion_sale_post_prompt'},
  {emoji: '🧙', titleKey: 'pmwjs_ai_chat_halloween_suggestion_witch_hat_lettering', promptKey: 'pmwjs_ai_chat_halloween_suggestion_witch_hat_lettering_prompt'},
  {emoji: '✍️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_costume_contest_caption', promptKey: 'pmwjs_ai_chat_halloween_suggestion_costume_contest_caption_prompt'},
];

const AI_CHAT_HALLOWEEN_INSPIRATIONS: AiChatSeasonInspiration[] = [
  {card: AiChatHalloweenCard.BAKERY_MENU, visualLayoutVariant: InspirationLayoutVariant.Centered, widthVariant: InspirationWidthVariant.Medium, pillTextKey: 'pmwjs_ai_chat_inspiration_design', pillIcon: 'icon-designs', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_1'},
  {card: AiChatHalloweenCard.PARTY_POSTER, visualLayoutVariant: InspirationLayoutVariant.Centered, widthVariant: InspirationWidthVariant.Medium, pillTextKey: 'pmwjs_ai_chat_inspiration_images', pillIcon: 'icon-image', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_2'},
  {card: AiChatHalloweenCard.PROMO_IDEAS, visualLayoutVariant: InspirationLayoutVariant.FullWidth, widthVariant: InspirationWidthVariant.Narrow, pillTextKey: 'pmwjs_ai_chat_inspiration_marketing_ideas', pillIcon: 'icon-megaphone', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_3'},
  {card: AiChatHalloweenCard.CAPTION, visualLayoutVariant: InspirationLayoutVariant.FullWidth, widthVariant: InspirationWidthVariant.Narrow, pillTextKey: 'pmwjs_ai_chat_inspiration_captions', pillIcon: 'icon-ai-caption', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_4'},
];

const AI_CHAT_CHRISTMAS_SUGGESTIONS: AiChatSeasonSuggestion[] = [
  {emoji: '🎄', titleKey: 'pmwjs_ai_chat_christmas_suggestion_holiday_party_poster', promptKey: 'pmwjs_ai_chat_christmas_suggestion_holiday_party_poster_prompt'},
  {emoji: '⛄', titleKey: 'pmwjs_ai_chat_christmas_suggestion_cute_snowman_sticker', promptKey: 'pmwjs_ai_chat_christmas_suggestion_cute_snowman_sticker_prompt'},
  {emoji: '💡', titleKey: 'pmwjs_ai_chat_christmas_suggestion_promo_ideas', promptKey: 'pmwjs_ai_chat_christmas_suggestion_promo_ideas_prompt'},
  {emoji: '🎁', titleKey: 'pmwjs_ai_chat_christmas_suggestion_gift_guide_flyer', promptKey: 'pmwjs_ai_chat_christmas_suggestion_gift_guide_flyer_prompt'},
  {emoji: '🦌', titleKey: 'pmwjs_ai_chat_christmas_suggestion_reindeer_logo', promptKey: 'pmwjs_ai_chat_christmas_suggestion_reindeer_logo_prompt'},
  {emoji: '✍️', titleKey: 'pmwjs_ai_chat_christmas_suggestion_festive_instagram_caption', promptKey: 'pmwjs_ai_chat_christmas_suggestion_festive_instagram_caption_prompt'},
  {emoji: '🎅', titleKey: 'pmwjs_ai_chat_christmas_suggestion_santa_photo_booth_sign', promptKey: 'pmwjs_ai_chat_christmas_suggestion_santa_photo_booth_sign_prompt'},
  {emoji: '☕', titleKey: 'pmwjs_ai_chat_christmas_suggestion_cozy_cafe_photo', promptKey: 'pmwjs_ai_chat_christmas_suggestion_cozy_cafe_photo_prompt'},
  {emoji: '🍪', titleKey: 'pmwjs_ai_chat_christmas_suggestion_cookie_swap_flyer', promptKey: 'pmwjs_ai_chat_christmas_suggestion_cookie_swap_flyer_prompt'},
  {emoji: '🧸', titleKey: 'pmwjs_ai_chat_christmas_suggestion_toy_drive_flyer', promptKey: 'pmwjs_ai_chat_christmas_suggestion_toy_drive_flyer_prompt'},
  {emoji: '✨', titleKey: 'pmwjs_ai_chat_christmas_suggestion_merry_christmas_lettering', promptKey: 'pmwjs_ai_chat_christmas_suggestion_merry_christmas_lettering_prompt'},
  {emoji: '⛪', titleKey: 'pmwjs_ai_chat_christmas_suggestion_christmas_service_invite', promptKey: 'pmwjs_ai_chat_christmas_suggestion_christmas_service_invite_prompt'},
];

const AI_CHAT_CHRISTMAS_INSPIRATIONS: AiChatSeasonInspiration[] = [
  {card: AiChatChristmasCard.BAKERY_MENU, visualLayoutVariant: InspirationLayoutVariant.Centered, widthVariant: InspirationWidthVariant.Medium, pillTextKey: 'pmwjs_ai_chat_inspiration_design', pillIcon: 'icon-designs', promptKey: 'pmwjs_ai_chat_christmas_inspiration_prompt_1'},
  {card: AiChatChristmasCard.PROMO_IDEAS, visualLayoutVariant: InspirationLayoutVariant.FullWidth, widthVariant: InspirationWidthVariant.Narrow, pillTextKey: 'pmwjs_ai_chat_inspiration_marketing_ideas', pillIcon: 'icon-megaphone', promptKey: 'pmwjs_ai_chat_christmas_inspiration_prompt_3'},
  {card: AiChatChristmasCard.PARTY_POSTER, visualLayoutVariant: InspirationLayoutVariant.Centered, widthVariant: InspirationWidthVariant.Medium, pillTextKey: 'pmwjs_ai_chat_inspiration_images', pillIcon: 'icon-image', promptKey: 'pmwjs_ai_chat_christmas_inspiration_prompt_2'},
  {card: AiChatChristmasCard.CAPTION, visualLayoutVariant: InspirationLayoutVariant.FullWidth, widthVariant: InspirationWidthVariant.Narrow, pillTextKey: 'pmwjs_ai_chat_inspiration_captions', pillIcon: 'icon-ai-caption', promptKey: 'pmwjs_ai_chat_christmas_inspiration_prompt_4'},
];

export const AI_CHAT_SEASON_CONFIGS: Record<AiChatSeason, AiChatSeasonConfig> = {
  [AiChatSeason.HALLOWEEN]: {
    dates: {startMonthIndex: 9, startDay: 1, endMonthIndex: 9, endDay: 31},
    suggestions: AI_CHAT_HALLOWEEN_SUGGESTIONS,
    inspirations: AI_CHAT_HALLOWEEN_INSPIRATIONS,
    loginButtonType: ButtonType.SECONDARY,
  },
  [AiChatSeason.CHRISTMAS]: {
    dates: {startMonthIndex: 10, startDay: 1, endMonthIndex: 11, endDay: 25},
    suggestions: AI_CHAT_CHRISTMAS_SUGGESTIONS,
    inspirations: AI_CHAT_CHRISTMAS_INSPIRATIONS,
    loginButtonType: ButtonType.SECONDARY,
  },
};
